import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  XMarkIcon,
  ClockIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FireIcon,
  InformationCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { odometerService } from '../../services/logistics/odometer';
import { odometerAutomationService } from '../../services/logistics/odometer-automation';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const OdometerReadingModal = ({ 
  isOpen, 
  onClose, 
  vehicle, 
  onReadingAdded 
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reading: '',
    notes: ''
  });
  const [errors, setErrors] = useState([]);
  const [lastReading, setLastReading] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showSyncSection, setShowSyncSection] = useState(false);

  const checkSyncStatus = useCallback(async () => {
    if (!vehicle || !user?.companyId) return;
    
    try {
      const status = await odometerAutomationService.getOdometerSyncStatus(user.companyId);
      setSyncStatus(status);
    } catch (error) {
      console.error('Error checking sync status:', error);
    }
  }, [vehicle, user?.companyId]);

  useEffect(() => {
    if (isOpen && vehicle) {
      // Get the last odometer reading for validation
      setLastReading(vehicle.latestOdometerReading);
      setFormData({
        reading: '',
        notes: ''
      });
      setErrors([]);
      
      // Check sync status for this vehicle
      checkSyncStatus();
    }
  }, [isOpen, vehicle, checkSyncStatus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = () => {
    const newErrors = [];
    
    if (!formData.reading || formData.reading.trim() === '') {
      newErrors.push(t('vehicles.odometer_reading_required', 'Odometer reading is required'));
    }
    
    const reading = parseFloat(formData.reading);
    if (isNaN(reading) || reading <= 0) {
      newErrors.push(t('vehicles.odometer_reading_invalid', 'Odometer reading must be a positive number'));
    }
    
    // Validate against last reading
    if (lastReading && reading < lastReading.reading) {
      newErrors.push(
        t('vehicles.odometer_reading_too_low', 
          `New reading (${reading}) cannot be less than last reading (${lastReading.reading})`
        )
      );
    }
    
    // Check for unrealistic jumps
    if (lastReading && (reading - lastReading.reading) > 10000) {
      newErrors.push(
        t('vehicles.odometer_reading_too_high', 
          `Reading increase of ${reading - lastReading.reading} km seems unusually high`
        )
      );
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const readingData = {
        reading: parseFloat(formData.reading),
        source: 'manual',
        notes: formData.notes.trim() || null,
        recordedBy: user.uid
      };

      await odometerService.addReading(vehicle.companyId, vehicle.id, readingData);
      
      toast.success(t('vehicles.odometer_reading_added', 'Odometer reading added successfully'));
      
      if (onReadingAdded) {
        onReadingAdded();
      }
      
      onClose();
    } catch (error) {
      console.error('Error adding odometer reading:', error);
      toast.error(t('vehicles.odometer_reading_error', 'Failed to add odometer reading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSyncFromFuel = async () => {
    if (!vehicle || !user?.companyId) return;
    
    setSyncing(true);
    try {
      const result = await odometerAutomationService.syncOdometerFromFuelTransactions(
        user.companyId,
        { vehicleId: vehicle.id }
      );
      
      if (result.created > 0) {
        toast.success(
          t('vehicles.odometer_sync_success', 
            `Successfully synced ${result.created} odometer readings from fuel data`
          )
        );
        
        // Refresh sync status
        await checkSyncStatus();
        
        if (onReadingAdded) {
          onReadingAdded();
        }
      } else {
        toast.info(
          t('vehicles.odometer_sync_no_new', 
            'No new odometer readings found to sync'
          )
        );
      }
      
      if (result.errors.length > 0) {
        console.warn('Sync errors:', result.errors);
        toast.error(
          t('vehicles.odometer_sync_partial_error', 
            `Sync completed with ${result.errors.length} errors`
          )
        );
      }
      
    } catch (error) {
      console.error('Error syncing odometer readings:', error);
      toast.error(
        t('vehicles.odometer_sync_error', 
          'Failed to sync odometer readings from fuel data'
        )
      );
    } finally {
      setSyncing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <TruckIcon className="h-5 w-5 mr-2" />
            {t('vehicles.add_odometer_reading', 'Add Odometer Reading')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {vehicle && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm font-medium text-gray-900">
              {vehicle.plateNumber}
            </div>
            <div className="text-sm text-gray-600">
              {vehicle.make} {vehicle.model}
            </div>
            {lastReading && (
              <div className="text-sm text-gray-500 mt-1 flex items-center">
                <ClockIcon className="h-4 w-4 mr-1" />
                {t('vehicles.last_reading', 'Last reading')}: {lastReading.reading.toLocaleString()} km
                <span className="ml-2 text-xs">
                  ({new Date(lastReading.recordedAt).toLocaleDateString()})
                </span>
              </div>
            )}
          </div>
        )}

        {/* Automatic Sync Section */}
        {syncStatus && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <FireIcon className="h-5 w-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-900">
                  {t('vehicles.automatic_odometer_sync', 'Automatic Odometer Sync')}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowSyncSection(!showSyncSection)}
                className="text-blue-600 hover:text-blue-800"
              >
                <InformationCircleIcon className="h-4 w-4" />
              </button>
            </div>
            
            {showSyncSection && (
              <div className="space-y-2">
                <div className="text-xs text-blue-700">
                  {t('vehicles.sync_status_info', 
                    `${syncStatus.totalOdometerFromFuel} of ${syncStatus.totalFuelWithOdometer} fuel transactions have odometer readings synced (${syncStatus.syncPercentage}%)`
                  )}
                </div>
                
                {syncStatus.needsSync && (
                  <button
                    type="button"
                    onClick={handleSyncFromFuel}
                    disabled={syncing}
                    className="flex items-center px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {syncing ? (
                      <>
                        <LoadingSpinner size="xs" />
                        <span className="ml-1">
                          {t('vehicles.syncing', 'Syncing...')}
                        </span>
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="h-3 w-3 mr-1" />
                        {t('vehicles.sync_from_fuel', 'Sync from Fuel Data')}
                      </>
                    )}
                  </button>
                )}
                
                {!syncStatus.needsSync && (
                  <div className="text-xs text-green-700 flex items-center">
                    <CheckIcon className="h-3 w-3 mr-1" />
                    {t('vehicles.sync_up_to_date', 'All fuel odometer readings are synced')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {errors.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-sm font-medium text-red-800">
                {t('common.validation_errors', 'Please fix the following errors:')}
              </span>
            </div>
            <ul className="text-sm text-red-700 space-y-1">
              {errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">
              {t('vehicles.odometer_reading', 'Odometer Reading')} (km) *
            </label>
            <input
              type="number"
              name="reading"
              value={formData.reading}
              onChange={handleInputChange}
              className={`input-field ${errors.length > 0 ? 'border-red-300' : ''}`}
              placeholder={t('vehicles.enter_odometer_reading', 'Enter current odometer reading')}
              min="0"
              step="0.1"
              required
            />
          </div>

          <div>
            <label className="label">
              {t('vehicles.notes', 'Notes')} ({t('common.optional', 'Optional')})
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="input-field"
              rows={3}
              placeholder={t('vehicles.odometer_notes_placeholder', 'Add any notes about this reading...')}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">{t('common.saving', 'Saving...')}</span>
                </div>
              ) : (
                t('vehicles.add_reading', 'Add Reading')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OdometerReadingModal;